import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { TRequestStatus } from "@models/msc.model";
import productsService from "@/services/products.service";
import { TProduct, TProductCategory } from "@/models/products.model";
import { PRODUCT_STATUS_ACTIVE, PRODUCT_STATUS_ARCHIVED } from "@icebergeradmin/common";

type ProductPayload = Omit<TProduct, "created" | "updated" | "image"> & { image?: string };

type ProductsState = {
  initialized: boolean;
  status: TRequestStatus;
  products: TProduct[];
  categories: TProductCategory[];
};

type ProductsActions = {
  getAllProducts: () => Promise<TProduct[]>;
  saveProduct: (data: ProductPayload) => Promise<TProduct>;
  updateProduct: (id: number, data: ProductPayload) => Promise<TProduct>;
  archiveProduct: (id: number) => Promise<void>;
  unarchiveProduct: (id: number) => Promise<void>;
  deleteProduct: (id: number) => Promise<void>;
  saveProductCategory: (name: string) => Promise<TProductCategory>;
};

export const useProductsStore = create<ProductsState & ProductsActions>()(
  devtools(
    (set, get) => ({
      initialized: false,
      status: "IDLE",
      products: [],
      categories: [],

      getAllProducts: async () => {
        const { products, initialized } = get();
        if (initialized) return products;

        set({ status: "PENDING" }, false, "getAllProducts/pending");
        const categories = await productsService.getCategories();
        const response = await productsService.getProducts();
        set(
          { categories, products: response, status: "IDLE", initialized: true },
          false,
          "getAllProducts/fulfilled"
        );
        return response;
      },

      saveProduct: async (data) => {
        const response = await productsService.saveProduct(data);
        set(
          (state) => ({ products: [...state.products, response] }),
          false,
          "saveProduct/fulfilled"
        );
        return response;
      },

      updateProduct: async (id, data) => {
        const response = await productsService.updateProduct(id, data);
        set(
          (state) => ({
            products: state.products.map((t) => (t.id === response.id ? response : t)),
          }),
          false,
          "updateProduct/fulfilled"
        );
        return response;
      },

      archiveProduct: async (id) => {
        await productsService.archiveProduct(id);
        set(
          (state) => ({
            products: state.products.map((t) =>
              t.id === id ? { ...t, status: PRODUCT_STATUS_ARCHIVED } : t
            ),
          }),
          false,
          "archiveProduct/fulfilled"
        );
      },

      unarchiveProduct: async (id) => {
        await productsService.unarchiveProduct(id);
        set(
          (state) => ({
            products: state.products.map((t) =>
              t.id === id ? { ...t, status: PRODUCT_STATUS_ACTIVE } : t
            ),
          }),
          false,
          "unarchiveProduct/fulfilled"
        );
      },

      // Optimistic removal: drop the product immediately, restore on API failure.
      deleteProduct: async (id) => {
        const productsForRollback = get().products;
        set(
          (state) => ({ products: state.products.filter((t) => t.id !== id) }),
          false,
          "deleteProduct/optimistic"
        );

        try {
          await productsService.deleteProduct(id);
        } catch (error) {
          set(
            { products: productsForRollback, status: "IDLE" },
            false,
            "deleteProduct/rollback"
          );
          throw error;
        }
      },

      saveProductCategory: async (name) => {
        const response = await productsService.saveCategory(name);
        set(
          (state) => ({ categories: [...state.categories, response] }),
          false,
          "saveProductCategory/fulfilled"
        );
        return response;
      },
    }),
    { name: "products" }
  )
);
